import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { value: 'simple', label: 'Simple (website)' },
  { value: 'kiosk', label: 'Kiosk (touch)' },
  { value: 'pro', label: 'Pro (full handicapper)' },
];
const BRANDING = [
  { value: 'intellitix', label: 'IntelliTix branded' },
  { value: 'neutral', label: 'Neutral' },
];

const ALGO_LABELS = { inherit: 'Inherit (Default)', original: 'Original', logarithmic: 'Logarithmic' };

const EMPTY = {
  clientId: '', name: '', mode: 'simple', allowCombos: true,
  branding: 'intellitix', amountPresets: '20, 30, 40, 50, 75, 100, 150, 200, 300, 500',
  ruleSet: 'A', algo: 'inherit', pixTickerEnabled: false, pixBombThreshold: 50,
};

export default function WidgetClients() {
  const [clients, setClients] = useState([]);
  const [ruleSets, setRuleSets] = useState(['A']);
  const [algoOptions, setAlgoOptions] = useState(['inherit', 'original', 'logarithmic']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sample, setSample] = useState(null); // {track, race, date} for live preview
  const [pixImporting, setPixImporting] = useState(false);
  const [pixStatus, setPixStatus] = useState('');
  const [pixLastRun, setPixLastRun] = useState(null);

  const fetchPixStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/quickpick/pixticker/import/status`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.imported_at) setPixLastRun(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchPixStatus(); }, [fetchPixStatus]);

  const fmtWhen = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ET';
    } catch { return iso; }
  };

  const importPixticker = async () => {
    setPixImporting(true);
    setPixStatus('');
    try {
      const res = await fetch(`${API_URL}/api/quickpick/pixticker/import`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setPixStatus(`PixTicker CSV imported for ${data.date} — ${data.upserted} race(s) updated, ${data.skipped_final} official race(s) left frozen.`);
        fetchPixStatus();
      } else {
        setPixStatus(`Import failed: ${data.error || 'unknown error'} (check the file exists in emCopy as w3temppicks.csv).`);
      }
    } catch (err) {
      setPixStatus(`Import error: ${err.message}`);
    } finally {
      setPixImporting(false);
    }
  };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/quickpick/widget-clients`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load widget clients');
      const data = await res.json();
      setClients(data.clients || []);
      if (Array.isArray(data.ruleSets) && data.ruleSets.length) setRuleSets(data.ruleSets);
      if (Array.isArray(data.algoOptions) && data.algoOptions.length) setAlgoOptions(data.algoOptions);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Grab the first available race so "Preview" opens against real data.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/quickpick/races`);
        if (!res.ok) return;
        const data = await res.json();
        const t = (data.tracks || [])[0];
        const r = t && (t.races || [])[0];
        if (t && r) setSample({ track: t.code, race: r.raceNumber, date: data.date });
      } catch { /* no races available */ }
    })();
  }, []);

  const preview = (c) => {
    if (!sample) { setError('No live races available to preview right now.'); return; }
    const url = `${window.location.origin}/widget?clientId=${encodeURIComponent(c.clientId)}&track=${sample.track}&race=${sample.race}&date=${sample.date}`;
    window.open(url, '_blank', 'noopener');
  };

  const openNew = () => {
    setIsNew(true);
    setForm(EMPTY);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setIsNew(false);
    setForm({
      clientId: c.clientId,
      name: c.name || '',
      mode: c.mode || 'simple',
      allowCombos: c.allowCombos !== false,
      branding: c.branding || 'intellitix',
      amountPresets: (c.amountPresets && c.amountPresets.length ? c.amountPresets : EMPTY.amountPresets.split(',').map(s => +s.trim())).join(', '),
      ruleSet: c.ruleSet || 'A',
      algo: c.algo || 'inherit',
      pixTickerEnabled: c.pixTickerEnabled === true,
      pixBombThreshold: c.pixBombThreshold != null ? c.pixBombThreshold : 50,
    });
    setShowModal(true);
  };

  const save = async () => {
    setError(''); setSuccess('');
    const clientId = form.clientId.trim();
    if (!clientId) { setError('Client ID is required'); return; }
    const presets = form.amountPresets
      .split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
    if (!presets.length) { setError('Add at least one valid amount preset'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/quickpick/widget-clients`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          clientId,
          name: form.name.trim() || null,
          mode: form.mode,
          allowCombos: form.allowCombos,
          branding: form.branding,
          amountPresets: presets,
          ruleSet: form.ruleSet,
          algo: form.algo,
          pixTickerEnabled: form.pixTickerEnabled,
          pixBombThreshold: Number(form.pixBombThreshold) || 50,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save client');
      setSuccess(`Client "${clientId}" saved.`);
      setShowModal(false);
      fetchClients();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete widget client "${c.clientId}"? Embeds using it will fall back to defaults.`)) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_URL}/api/quickpick/widget-clients/${encodeURIComponent(c.clientId)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete client');
      setSuccess(`Client "${c.clientId}" deleted.`);
      fetchClients();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyEmbed = (c) => {
    const url = `${window.location.origin}/widget?clientId=${encodeURIComponent(c.clientId)}&track=TRACK&race=1`;
    navigator.clipboard?.writeText(url);
    setSuccess(`Embed URL for "${c.clientId}" copied (replace TRACK/race).`);
  };

  const modeBadge = (m) => ({
    simple: 'bg-sky-500/20 text-sky-400 border-sky-500/50',
    kiosk: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
    pro: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  }[m] || 'bg-slate-500/20 text-slate-400 border-slate-500/50');

  return (
    <div data-testid="widget-clients-tab">
      {error && <div className="mb-4 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg" data-testid="wc-error">{error}</div>}
      {success && <div className="mb-4 bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-4 py-3 rounded-lg" data-testid="wc-success">{success}</div>}

      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Widget Clients</h1>
          <p className="text-slate-400 text-sm">Per-client B2B embed configs. Each <code className="text-slate-300">clientId</code> controls mode, branding, combos and bet presets.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={importPixticker} disabled={pixImporting}
            className="px-4 py-2 bg-lime-600 hover:bg-lime-500 disabled:opacity-50 text-white font-medium rounded-lg transition" data-testid="wc-import-pixticker-btn">
            {pixImporting ? 'Importing…' : 'Import PixTicker CSV'}
          </button>
          <button onClick={openNew} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition" data-testid="wc-new-btn">
            + New Client
          </button>
        </div>
      </div>
      {pixLastRun && (
        <div className="mb-2 text-xs text-slate-400" data-testid="wc-pix-lastrun">
          PixTicker CSV last processed: <span className="text-slate-200 font-medium">{fmtWhen(pixLastRun.imported_at)}</span>
          {' · '}race day {pixLastRun.date} · {pixLastRun.upserted} updated, {pixLastRun.skipped_final} official frozen
        </div>
      )}
      {pixStatus && (
        <div className="mb-4 text-sm text-slate-300 bg-slate-800/60 border border-slate-700 rounded-lg px-4 py-2" data-testid="wc-pix-status">
          {pixStatus}
        </div>
      )}

      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Client ID</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Name</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Mode</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Combos</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Branding</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Rule Set</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Algorithm</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">PixTicker</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Presets</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-400">No widget clients yet. Click "New Client" to add one.</td></tr>
              ) : clients.map((c) => (
                <tr key={c.clientId} className="hover:bg-slate-800/50" data-testid={`wc-row-${c.clientId}`}>
                  <td className="px-4 py-3"><span className="px-2 py-1 text-sm font-mono font-medium rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/50">{c.clientId}</span></td>
                  <td className="px-4 py-3 text-white">{c.name || '-'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded border ${modeBadge(c.mode)}`}>{c.mode || 'simple'}</span></td>
                  <td className="px-4 py-3 text-slate-300 text-sm">{c.allowCombos !== false ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-3 text-slate-300 text-sm">{c.branding || 'intellitix'}</td>
                  <td className="px-4 py-3"><span className="px-2 py-1 text-xs font-mono font-medium rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/50">{c.ruleSet || 'A'}</span></td>
                  <td className="px-4 py-3"><span className="px-2 py-1 text-xs font-medium rounded bg-teal-500/20 text-teal-300 border border-teal-500/50">{ALGO_LABELS[c.algo] || 'Inherit (Default)'}</span></td>
                  <td className="px-4 py-3">{c.pixTickerEnabled === true ? <span className="px-2 py-1 text-xs font-medium rounded bg-lime-500/20 text-lime-300 border border-lime-500/50">On</span> : <span className="text-slate-500 text-sm">Off</span>}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs max-w-[200px] truncate">{(c.amountPresets || []).map(a => `$${a}`).join(', ') || '(defaults)'}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => preview(c)} className="px-2 py-1 text-xs bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 rounded transition mr-2" data-testid={`wc-preview-${c.clientId}`} title={sample ? `Preview on ${sample.track} R${sample.race}` : 'No live race'}>Preview</button>
                    <button onClick={() => copyEmbed(c)} className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded transition mr-2" data-testid={`wc-embed-${c.clientId}`}>Embed</button>
                    <button onClick={() => openEdit(c)} className="px-3 py-1 text-sm bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded transition mr-2" data-testid={`wc-edit-${c.clientId}`}>Edit</button>
                    {c.clientId === 'TEST001' ? (
                      <span className="px-3 py-1 text-sm text-slate-600 cursor-not-allowed" title="Protected: this is the IntelliTix main app" data-testid={`wc-delete-${c.clientId}`}>Protected</span>
                    ) : (
                      <button onClick={() => remove(c)} className="px-3 py-1 text-sm bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded transition" data-testid={`wc-delete-${c.clientId}`}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md" data-testid="wc-modal">
            <h3 className="text-xl font-semibold text-white mb-4">{isNew ? 'New Widget Client' : `Edit "${form.clientId}"`}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Client ID</label>
                <input
                  type="text" value={form.clientId} disabled={!isNew}
                  onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                  placeholder="e.g. ACME001"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="wc-field-clientId"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Display Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ACME Casino"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="wc-field-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Mode</label>
                  <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="wc-field-mode">
                    {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Branding</label>
                  <select value={form.branding} onChange={(e) => setForm({ ...form, branding: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="wc-field-branding">
                    {BRANDING.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Rule Set</label>
                  <select value={form.ruleSet} onChange={(e) => setForm({ ...form, ruleSet: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="wc-field-ruleset">
                    {ruleSets.map(rs => <option key={rs} value={rs}>{`Rule Set ${rs}`}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Algorithm</label>
                  <select value={form.algo} onChange={(e) => setForm({ ...form, algo: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="wc-field-algo">
                    {algoOptions.map(a => <option key={a} value={a}>{ALGO_LABELS[a] || a}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Bet Amount Presets (comma-separated)</label>
                <input type="text" value={form.amountPresets} onChange={(e) => setForm({ ...form, amountPresets: e.target.value })}
                  placeholder="5, 10, 20, 50, 100"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="wc-field-presets" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.allowCombos} onChange={(e) => setForm({ ...form, allowCombos: e.target.checked })}
                  className="w-4 h-4 accent-emerald-500" data-testid="wc-field-combos" />
                Allow combo wagers (EX+TRI, etc.)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.pixTickerEnabled} onChange={(e) => setForm({ ...form, pixTickerEnabled: e.target.checked })}
                  className="w-4 h-4 accent-emerald-500" data-testid="wc-field-pixticker" />
                Enable PixTicker (raw-data plays/results scroller)
              </label>
              {form.pixTickerEnabled && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Big-score icon threshold ($ payout)</label>
                  <input type="number" min="0" value={form.pixBombThreshold} onChange={(e) => setForm({ ...form, pixBombThreshold: e.target.value })}
                    placeholder="50"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="wc-field-bomb" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition" data-testid="wc-cancel-btn">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-medium rounded-lg transition" data-testid="wc-save-btn">
                {saving ? 'Saving...' : 'Save Client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
